import React from 'react';
import { useNavigate } from 'react-router-dom';
import JobStats from './Jobs/JobStats';
import './ProfileSetupTimeline.css';

const ProfileSetupTimeline = ({ 
  user, 
  completionStatus, 
  profileCompletion,
  userSkills,
  userProjects,
  setShowEditModal,
  setShowSkillsPopup,
  setShowProjectPopup,
  onAccountSettingsClick
}) => {
  const navigate = useNavigate();
  const currentMode = user?.active_mode || user?.default_mode || 'personal';
  const isCompanyMode = currentMode === 'company';
  const isRecruiterMode = currentMode === 'recruiter';
  const isRecruiterOrCompanyMode = isCompanyMode || isRecruiterMode;
  
  // Calculate completion percentage
  const totalSteps = 5; // email, timezone, skills, title, bio
  const completedSteps = [
    user?.is_email_verified,
    user?.timezone,
    userSkills && userSkills.length > 0,
    user?.professional_title,
    user?.bio
  ].filter(Boolean).length;
  
  const completionPercentage = Math.round((completedSteps / totalSteps) * 100);
  const projectsCount = userProjects ? userProjects.length : 0;

  return (
    <div className="profile-setup-card">
      <div className="profile-setup-section">
        <div className="profile-setup-header">
          <div className="header-content">
            <h3>Profile Completion</h3>
          </div>
        </div>

        
        {/* Creative Stats Grid */}
        <div className="stats-grid">
          {/* Step 1: Email Verification */}
          <div className={`stat-card email-card ${user?.is_email_verified ? 'completed' : 'pending'}`}>
            <div className="card-content">
              <div className="card-title-row">
                <h4 className="card-title">Email Verification</h4>
                {user?.is_email_verified ? (
                  <span className="status-badge success">Verified</span>
                ) : (
                  <span className="status-badge warning">Pending</span>
                )}
              </div>
              <p className="card-description">
                {user?.is_email_verified 
                  ? "Your email has been verified" 
                  : "Verify your email address to secure your account"
                }
              </p>
              {!user?.is_email_verified && (
                <button 
                  className="action-btn primary"
                  onClick={() => navigate('/settings?tab=email')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span className="btn-text">Verify Email</span>
                </button>
              )}
            </div>
          </div>

          {/* Step 2: Set Timezone */}
          <div className={`stat-card timezone-card ${user?.timezone ? 'completed' : 'pending'}`}>
            <div className="card-content">
              <div className="card-title-row">
                <h4 className="card-title">Timezone</h4>
                {user?.timezone ? (
                  <span className="status-badge success">Set</span>
                ) : (
                  <span className="status-badge warning">Not Set</span>
                )}
              </div>
              <p className="card-description">
                {user?.timezone 
                  ? `Set to ${user.timezone_display || user.timezone}` 
                  : "Set your preferred timezone for better collaboration"
                }
              </p>
              {!user?.timezone && user?.is_email_verified && (
                <button 
                  className="action-btn secondary"
                  onClick={() => setShowEditModal(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Step 3: Add Skills */}
          <div className={`stat-card skills-card ${userSkills && userSkills.length > 0 ? 'completed' : 'pending'}`}>
            <div className="card-content">
              <div className="card-title-row">
                <h4 className="card-title">Skills</h4>
                {userSkills && userSkills.length > 0 ? (
                  <span className="status-badge success">Added</span>
                ) : (
                  <span className="status-badge warning">Not Added</span>
                )}
              </div>
              <p className="card-description">
                {userSkills && userSkills.length > 0 
                  ? `You have ${userSkills.length} skill${userSkills.length !== 1 ? 's' : ''} added` 
                  : "Add your professional skills and expertise"
                }
              </p>
              {(!userSkills || userSkills.length === 0) && user?.timezone && (
                <button 
                  className="action-btn tertiary"
                  onClick={() => setShowSkillsPopup(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                    <path d="M2 17l10 5 10-5"></path>
                    <path d="M2 12l10 5 10-5"></path>
                  </svg>
                  <span className="btn-text">Add Skills</span>
                </button>
              )}
            </div>
          </div>

          {/* Step 4: Professional Title */}
          <div className={`stat-card title-card ${user?.professional_title ? 'completed' : 'pending'}`}>
            <div className="card-content">
              <div className="card-title-row">
                <h4 className="card-title">Professional Title</h4>
                {user?.professional_title ? (
                  <span className="status-badge success">Added</span>
                ) : (
                  <span className="status-badge warning">Not Added</span>
                )}
              </div>
              <p className="card-description">
                {user?.professional_title 
                  ? `"${user.professional_title}"` 
                  : "Add your professional title or headline"
                }
              </p>
              {!user?.professional_title && userSkills && userSkills.length > 0 && (
                <button 
                  className="action-btn quaternary"
                  onClick={() => setShowEditModal(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14,2 14,8 20,8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10,9 9,9 8,9"></polyline>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Step 5: About Story */}
          <div className={`stat-card bio-card ${user?.bio ? 'completed' : 'pending'}`}>
            <div className="card-content">
              <div className="card-title-row">
                <h4 className="card-title">About Me</h4>
                {user?.bio ? (
                  <span className="status-badge success">Added</span>
                ) : (
                  <span className="status-badge warning">Not Added</span>
                )}
              </div>
              <p className="card-description">
                {user?.bio 
                  ? "Your bio has been added" 
                  : "Write a brief description about yourself"
                }
              </p>
              {!user?.bio && user?.professional_title && (
                <button 
                  className="action-btn quinary"
                  onClick={() => setShowEditModal(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span className="btn-text">Add Bio</span>
                </button>
              )}
            </div>
          </div>

          {/* Step 6: Projects Portfolio (Optional) */}
          <div className={`stat-card projects-card ${userProjects && userProjects.length > 0 ? 'completed' : 'pending'}`}>
            <div className="card-content">
              <div className="card-title-row">
                <h4 className="card-title">Projects</h4>
                {userProjects && userProjects.length > 0 ? (
                  <span className="status-badge success">Added</span>
                ) : (
                  <span className="status-badge warning">Not Added</span>
                )}
              </div>
              <p className="card-description">
                {userProjects && userProjects.length > 0 
                  ? `You have ${userProjects.length} project${userProjects.length !== 1 ? 's' : ''} in your portfolio` 
                  : 'Optional: Showcase your work and projects'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Job Statistics - Show in Recruiter/Company Mode */}
        {isRecruiterOrCompanyMode && (
          <JobStats user={user} />
        )}
       
      </div>
    </div>
  );
};

export default ProfileSetupTimeline;

